import { useEffect, useRef, type KeyboardEvent, type ReactNode } from "react";
import { BodkyZhody } from "@/components/vyber/BodkyZhody";
import { cn } from "@/lib/utils";

/**
 * Tabuľka výberu z cenníka — jedna pre materiál, hranu, kovanie aj import
 * (KOLO 201). User: *„všetky tabuľky … sú rozdielne, inak vyzerajú, inak
 * filtrovanie funguje … chcel by som to ustáliť do jedného dizajnu"*.
 * Do vtedy mal každý výber vlastný zoznam: raz tabuľku, raz `<li>`
 * s rozmerom vpredu, raz tlačidlá, ktoré hranu vymenili hneď po kliku.
 *
 * - Riadok má pevnú výšku a dva riadky textu: názov so štítkami, pod ním
 *   sivým výrobca, pravý názov kovania či kategória. Kód, rozmer a cena
 *   sedia v stĺpcoch, takže sa dve položky dajú porovnať pohľadom.
 * - **Klik označí, dvojklik alebo Enter potvrdí** (rozhodnutie usera,
 *   platí aj pre hromadnú výmenu). Bez `onPotvrd` (výber vo formulári)
 *   je označenie samo výberom.
 * - Skupiny (V zákazke · Odporúčané · Ostatné z cenníka) sú oddeľovacie
 *   riadky v tej istej tabuľke — sticky hlavička platí pre všetky.
 * - Bodky zhody len pri skutočnom skóre (import).
 */

export interface RiadokVyberu {
  /** Jedinečný v tabuľke — id položky. */
  kluc: string;
  kod: string | null;
  /** Hlavný nápis — pri pomenovanom kovaní alias (KOLO 176). */
  nazov: string;
  /** Plný text do bubliny, keď je názov orezaný. */
  nazovTitle?: string;
  /** Druhý riadok — pravý názov kovania, výrobca, kategória. */
  pod?: string | null;
  stitky?: ReactNode;
  rozmer: string;
  cena: string;
  /** Posledný stĺpec — napr. „4× · 3,8 m" pri hranách zákazky. */
  info?: ReactNode;
  /**
   * Tlačidlo na konci riadku (★ na stránke Kovania, KOLO 202) — vidno ho
   * aj na mobile; klik naň riadok neoznačí ani nepotvrdí.
   */
  akcia?: ReactNode;
  zhoda?: { skore: number; dovod: string } | null;
  /** Prístupné meno riadku — kód a názov. */
  ariaLabel: string;
}

export interface SkupinaVyberu {
  kluc: string;
  nadpis: string;
  riadky: RiadokVyberu[];
}

/**
 * Ako prišlo označenie — `klik` (myš, medzerník, Enter bez potvrdenia)
 * je vôľa vybrať, `klaves` (šípka) len chodí po riadkoch. Zbalený výber
 * vo formulári sa po kliku zbalí, po šípke nie.
 */
export type AkoOznacene = "klik" | "klaves";

/** Vybraný riadok: podklad a pruh farbou appky. */
const VYBRANY = "bg-tandem-red-light font-medium hover:bg-tandem-red-light";
const PRUH = "shadow-[inset_3px_0_0_hsl(var(--primary))]";
// Celé reťazce — Tailwind triedu poskladanú za behu (`sm:${…}`) nenájde.
// Pruh je na prvej viditeľnej bunke: na mobile je kód skrytý.
const PRUH_KOD = "sm:shadow-[inset_3px_0_0_hsl(var(--primary))]";
const PRUH_NAZOV = "shadow-[inset_3px_0_0_hsl(var(--primary))] sm:shadow-none";

/** Ďalší kľúč pri šípke; bez výberu ↓ začne prvým, ↑ posledným. */
export function posunVyberu(
  kluce: readonly string[],
  vybrany: string | null,
  smer: 1 | -1,
): string | null {
  if (kluce.length === 0) return null;
  const i = vybrany === null ? -1 : kluce.indexOf(vybrany);
  if (i < 0) return smer === 1 ? kluce[0]! : kluce[kluce.length - 1]!;
  return kluce[Math.min(kluce.length - 1, Math.max(0, i + smer))]!;
}

/**
 * Šípky a Enter z poľa hľadania — kurzor ostane v poli, výber chodí po
 * riadkoch. Vráti `true`, keď kláves spracoval.
 */
export function klavesaVyberu(
  e: KeyboardEvent<HTMLElement>,
  {
    kluce,
    vybrany,
    onOznac,
    onPotvrd,
  }: {
    kluce: readonly string[];
    vybrany: string | null;
    onOznac: (kluc: string, ako: AkoOznacene) => void;
    onPotvrd?: (kluc: string) => void;
  },
): boolean {
  if (e.key === "ArrowDown" || e.key === "ArrowUp") {
    const dalsi = posunVyberu(kluce, vybrany, e.key === "ArrowDown" ? 1 : -1);
    if (dalsi === null) return false;
    e.preventDefault();
    onOznac(dalsi, "klaves");
    return true;
  }
  if (e.key === "Enter" && vybrany !== null && kluce.includes(vybrany)) {
    e.preventDefault();
    if (onPotvrd) onPotvrd(vybrany);
    else onOznac(vybrany, "klik");
    return true;
  }
  return false;
}

export function TabulkaVyberu({
  skupiny,
  vybrany,
  onOznac,
  onPotvrd,
  soZhodou = false,
  infoNadpis,
  stav,
  pata,
  className,
}: {
  skupiny: SkupinaVyberu[];
  vybrany: string | null;
  onOznac: (kluc: string, ako: AkoOznacene) => void;
  /** Dvojklik / Enter — vyber a potvrď. */
  onPotvrd?: (kluc: string) => void;
  /** Stĺpec Zhoda (import). */
  soZhodou?: boolean;
  /** Nadpis posledného stĺpca; stĺpec je, keď ho niektorý riadok má. */
  infoNadpis?: string;
  /** Namiesto riadkov — načítavanie, prázdny výsledok. */
  stav?: ReactNode;
  /** Pod tabuľkou — Načítať ďalšie. */
  pata?: ReactNode;
  /** Výška zoznamu (`max-h-…`). */
  className?: string;
}): JSX.Element {
  const obal = useRef<HTMLDivElement>(null);
  const plne = skupiny.filter((s) => s.riadky.length > 0);
  const riadky = plne.flatMap((s) => s.riadky);
  const kluce = riadky.map((r) => r.kluc);
  // Nadpisy skupín, keď ich volajúci dal viac — jediná skupina je len
  // „zoznam". Prázdna skupina sa nekreslí, nadpis ostatných áno: „V
  // zákazke" hovorí, odkiaľ hrany sú, aj keď cenník nič nenašiel.
  const sNadpismi = skupiny.length > 1;
  const maInfo = Boolean(infoNadpis) || riadky.some((r) => r.info != null && r.info !== "");
  const maAkciu = riadky.some((r) => r.akcia != null);
  const najlepsie = Math.max(0, ...riadky.map((r) => r.zhoda?.skore ?? 0));
  const stlpcov = 4 + (soZhodou ? 1 : 0) + (maInfo ? 1 : 0) + (maAkciu ? 1 : 0);

  // Vybraný riadok šípkou nesmie odísť z dohľadu. Posúva sa **len
  // zoznam**, nie okno či formulár okolo (`scrollIntoView` by posunul
  // všetkých predkov — formulár by pri otvorení skočil na picker).
  useEffect(() => {
    const box = obal.current;
    if (!box || vybrany === null) return;
    const el = najdiRiadok(box, vybrany);
    if (!el) return;
    const hlavicka = box.querySelector("thead")?.getBoundingClientRect().height ?? 0;
    const b = box.getBoundingClientRect();
    const r = el.getBoundingClientRect();
    if (r.top < b.top + hlavicka) box.scrollTop -= b.top + hlavicka - r.top;
    else if (r.bottom > b.bottom) box.scrollTop += r.bottom - b.bottom;
  }, [vybrany]);

  function klavesRiadku(e: KeyboardEvent<HTMLTableRowElement>, kluc: string): void {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      const dalsi = posunVyberu(kluce, kluc, e.key === "ArrowDown" ? 1 : -1);
      if (dalsi === null) return;
      e.preventDefault();
      onOznac(dalsi, "klaves");
      if (obal.current) najdiRiadok(obal.current, dalsi)?.focus();
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (onPotvrd) onPotvrd(kluc);
      else onOznac(kluc, "klik");
    } else if (e.key === " ") {
      e.preventDefault();
      onOznac(kluc, "klik");
    }
  }

  return (
    // `bg-background` musí byť **tu**: kontajner sa inak spolieha na
    // pozadie predka a pri vnorených dialógoch cez neho presvitá to, čo
    // je pod nimi.
    <div ref={obal} className={cn("overflow-y-auto rounded-md border bg-background", className)}>
      {stav ? (
        <div className="p-3 text-sm text-muted-foreground">{stav}</div>
      ) : (
        <table className="w-full table-fixed text-sm">
          {/* Pozadie patrí na `th`, nie na `thead`: pri `border-collapse:
              collapse` Chrome pozadie sekcie pri stickovaní nemaľuje
              a riadky idú viditeľne cez hlavičku. `z-10` je to isté, čo
              má `ui/table.tsx`. */}
          <thead className="sticky top-0 z-10 text-[11px] font-semibold uppercase tracking-wide text-foreground/70 [&_th]:bg-background-alt [&_th]:shadow-[inset_0_-1px_0_hsl(var(--border))]">
            <tr className="h-8">
              {soZhodou ? <th className="w-16 px-3 text-left">Zhoda</th> : null}
              {/* Na mobile kód odíde do druhého riadku — názov by inak
                  ostal na pár znakov. Stĺpec ostáva so šírkou 0, nie
                  `display: none`: nadpis skupiny má `colSpan` na všetky
                  stĺpce a skrytý stĺpec by z neho urobil prázdny navyše
                  (naživo názov 49 px na 390 px). */}
              <th className="w-0 overflow-hidden p-0 text-left sm:w-[150px] sm:px-3">
                <span className="hidden sm:inline">Kód</span>
              </th>
              <th className="px-3 text-left">Názov</th>
              <th className="w-[86px] whitespace-nowrap px-3 text-right">Rozmer</th>
              <th className="w-[104px] whitespace-nowrap px-3 text-right">Cena</th>
              {maInfo ? (
                <th className="w-0 overflow-hidden whitespace-nowrap p-0 text-right md:w-[104px] md:px-3">
                  <span className="hidden md:inline">{infoNadpis ?? ""}</span>
                </th>
              ) : null}
              {maAkciu ? (
                <th className="w-11 p-0">
                  <span className="sr-only">Akcia</span>
                </th>
              ) : null}
            </tr>
          </thead>
          {plne.map((s) => (
            <tbody key={s.kluc} aria-label={sNadpismi ? s.nadpis : undefined}>
              {sNadpismi ? (
                <tr aria-hidden>
                  <td
                    colSpan={stlpcov}
                    className="h-[30px] border-b border-border/70 bg-background-group px-3 text-[11px] font-bold uppercase tracking-wide text-foreground/80"
                  >
                    {s.nadpis}
                    <span className="ml-2 font-mono font-normal normal-case tracking-normal">
                      {s.riadky.length}
                    </span>
                  </td>
                </tr>
              ) : null}
              {s.riadky.map((r) => (
                <RiadokTabulky
                  key={r.kluc}
                  r={r}
                  vybrany={vybrany === r.kluc}
                  soZhodou={soZhodou}
                  najlepsie={najlepsie}
                  maInfo={maInfo}
                  maAkciu={maAkciu}
                  dvojklik={Boolean(onPotvrd)}
                  onOznac={() => onOznac(r.kluc, "klik")}
                  onPotvrd={onPotvrd ? () => onPotvrd(r.kluc) : undefined}
                  onKeyDown={(e) => klavesRiadku(e, r.kluc)}
                />
              ))}
            </tbody>
          ))}
        </table>
      )}
      {pata}
    </div>
  );
}

function najdiRiadok(box: HTMLElement, kluc: string): HTMLElement | undefined {
  return [...box.querySelectorAll<HTMLElement>("tr[data-kluc]")].find(
    (el) => el.dataset.kluc === kluc,
  );
}

function RiadokTabulky({
  r,
  vybrany,
  soZhodou,
  najlepsie,
  maInfo,
  maAkciu,
  dvojklik,
  onOznac,
  onPotvrd,
  onKeyDown,
}: {
  r: RiadokVyberu;
  vybrany: boolean;
  soZhodou: boolean;
  najlepsie: number;
  maInfo: boolean;
  maAkciu: boolean;
  dvojklik: boolean;
  onOznac: () => void;
  onPotvrd?: () => void;
  onKeyDown: (e: KeyboardEvent<HTMLTableRowElement>) => void;
}): JSX.Element {
  const pod = r.pod ?? "";
  return (
    <tr
      role="button"
      tabIndex={0}
      data-kluc={r.kluc}
      // Vybranosť nie je len farba pozadia (KOLO 120)
      aria-pressed={vybrany}
      aria-label={r.ariaLabel}
      onClick={onOznac}
      // Dvojklik spustí najprv dva kliky (označenie), až potom potvrdenie
      onDoubleClick={onPotvrd}
      onKeyDown={onKeyDown}
      className={cn(
        "h-[50px] cursor-pointer border-b border-border/50 outline-none last:border-b-0 hover:bg-background-alt focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
        // Dvojklik by inak označil slovo v názve
        dvojklik && "select-none",
        vybrany && VYBRANY,
      )}
    >
      {soZhodou ? (
        <td className={cn("px-3", vybrany && PRUH)}>
          {r.zhoda ? (
            <BodkyZhody skore={r.zhoda.skore} najlepsie={najlepsie} dovod={r.zhoda.dovod} />
          ) : null}
        </td>
      ) : null}
      <td
        className={cn(
          "truncate p-0 font-mono text-[13px] sm:px-3 [&>span]:hidden sm:[&>span]:inline",
          vybrany && !soZhodou && PRUH_KOD,
        )}
        title={r.kod ?? undefined}
      >
        <span>{r.kod ?? "—"}</span>
      </td>
      <td className={cn("px-3", vybrany && !soZhodou && PRUH_NAZOV)}>
        {/* Štítky ustúpia názvu: ten má aspoň 5rem, štítky sa orežú */}
        <div className="flex h-[18px] min-w-0 items-center gap-1.5 overflow-hidden">
          <span
            className="min-w-[5rem] truncate leading-[18px]"
            title={r.nazovTitle ?? r.nazov}
          >
            {r.nazov}
          </span>
          {r.stitky}
        </div>
        <div
          className="truncate text-xs font-normal leading-4 text-muted-foreground"
          title={pod || undefined}
        >
          {r.kod ? <span className="font-mono sm:hidden">{r.kod}{pod ? " · " : ""}</span> : null}
          {pod}
        </div>
      </td>
      <td className="whitespace-nowrap px-3 text-right font-mono text-[13px] text-foreground/80">
        {r.rozmer}
      </td>
      <td className="whitespace-nowrap px-3 text-right font-mono text-[13px]">{r.cena}</td>
      {maInfo ? (
        <td className="overflow-hidden whitespace-nowrap p-0 text-right text-xs text-foreground/80 md:px-3 [&>span]:hidden md:[&>span]:inline">
          <span>{r.info}</span>
        </td>
      ) : null}
      {maAkciu ? (
        // Akcia nie je výber riadku — klik, dvojklik ani Enter na nej
        // nesmú dôjsť k riadku
        <td
          className="p-0 text-center"
          onClick={(e) => e.stopPropagation()}
          onDoubleClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          {r.akcia}
        </td>
      ) : null}
    </tr>
  );
}
